import React from 'react';
import { Icon } from '../../../shared/utils/icons';
import { fullDate } from '../../../shared/utils/dates';
import SpeakerRow from '../../../shared/SpeakerRow';

// Access level -> badge icon + label. Mirrors the event page hero map; the
// canonical key ('public'|'members'|'members_guests'|'private') comes from
// Event::accessLevel().
const ACCESS = {
    PUBLIC:          { icon: 'fa-globe',           label: 'Public Event' },
    CHAPTER:         { icon: 'fa-user-plus',       label: 'Members + Guests' },
    GLOBAL_FORUM:    { icon: 'fa-users',           label: 'Global Forum' },
    WORKING_SESSION: { icon: 'fa-lock',            label: 'Private Working Session' },
};

// Date/time/location meta — matches the event page hero (.ch-hero__meta): FA icon
// in a navy chip + uppercase label + value.
const InfoItem = ({ icon, label, children }) => (
    <div className="modal-info">
        <i className={`fa-solid ${icon} modal-info-icon`} aria-hidden="true"></i>
        <div className="modal-info-text">
            <span>{label}</span>
            {children}
        </div>
    </div>
);

const EventModal = ({ event, onClose }) => {
    const open = !!event;

    const onBackdrop = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    // Split the trailing "(GMT-3)" off the time so it can render smaller/lighter,
    // matching the event page's .ch-hero__meta-gmt.
    const timeRange = event?.time_range || 'To be confirmed';
    const gmtMatch = timeRange.match(/\s*\((GMT[^)]*)\)\s*$/);
    const timeMain = gmtMatch ? timeRange.slice(0, gmtMatch.index).trim() : timeRange;
    const gmt = gmtMatch ? gmtMatch[1] : null;

    return (
        <div className={`modal-wrap${open ? ' is-open' : ''}`} onClick={onBackdrop}>
            {event && (
                <article className="modal" role="dialog" aria-modal="true" aria-label={event.title}>
                    <div className="modal-hero">
                        <button className="modal-close" onClick={onClose} aria-label="Close">
                            <Icon name="close" size={17} strokeWidth={2} />
                        </button>
                        <div className="modal-brand">
                            <img className="modal-brand__logo" src="/img/book-title.png" alt="Legal Operations KPIs" />
                            {event.chapter && (
                                <>
                                    <span className="modal-brand__rule" aria-hidden="true"></span>
                                    <span className="modal-brand__chapter">{event.chapter.name} Chapter</span>
                                </>
                            )}
                        </div>
                        <h2>{event.title}</h2>
                        {event.subtitle && <p>{event.subtitle}</p>}
                    </div>

                    <div className="modal-body">
                        <div className="modal-info-grid">
                            <InfoItem icon="fa-calendar-days" label="Date">
                                <strong>{fullDate(event.date)}</strong>
                            </InfoItem>
                            <InfoItem icon="fa-clock" label="Time">
                                <strong>
                                    {timeMain}
                                    {gmt && <span className="modal-info-gmt">{gmt}</span>}
                                </strong>
                            </InfoItem>
                            <InfoItem icon="fa-location-dot" label="Location">
                                <strong>{event.format === 'Remote' ? 'Remote' : event.location}</strong>
                            </InfoItem>
                            {ACCESS[event.access] && (
                                <InfoItem icon={ACCESS[event.access].icon} label="Access">
                                    <strong>{ACCESS[event.access].label}</strong>
                                </InfoItem>
                            )}
                        </div>

                        <div className="modal-cols">
                            <div>
                                {event.description && (
                                    <div className="modal-section">
                                        <h3>Event overview</h3>
                                        <div
                                            className="modal-rich"
                                            dangerouslySetInnerHTML={{ __html: event.description }}
                                        />
                                    </div>
                                )}
                                {event.takeaways?.trim() && (
                                    <div className="modal-section">
                                        <h3>What you&rsquo;ll take away</h3>
                                        <div
                                            className="modal-rich"
                                            dangerouslySetInnerHTML={{ __html: event.takeaways }}
                                        />
                                    </div>
                                )}
                            </div>

                            <aside>
                                {event.speakers?.length > 0 && (
                                    <div className="modal-section">
                                        <h3>Speakers</h3>
                                        <div className="ev-speakers">
                                            {event.speakers.map((s, i) => <SpeakerRow key={i} speaker={s} size="lg" />)}
                                        </div>
                                    </div>
                                )}

                                {event.themes?.length > 0 && (
                                    <div className="modal-section">
                                        <h3>Event themes</h3>
                                        <div className="topic-list">
                                            {event.themes.map((t, i) => (
                                                <span key={i} className="topic-tag">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </aside>
                        </div>
                    </div>

                    {event.event_url && (
                        <div className="modal-actions">
                            <a className="sp-ocard-btn" href={event.event_url}>
                                {event.is_past ? 'Event details' : 'Reserve a seat'} <i className="fa-solid fa-arrow-right"></i>
                            </a>
                        </div>
                    )}
                </article>
            )}
        </div>
    );
};

export default EventModal;
